import MojitoPresentation from 'mojito/presentation';
import MultiOddsBoostsDialog from './multi-odds-boosts-dialog/index.js';
import { useState, useMemo } from 'react';
import MojitoServices from 'mojito/services';
import { useDispatch } from 'react-redux';

const { CurrencyHelper } = MojitoPresentation.Utils;
const { actions: betslipActions } = MojitoServices.Betslip;
const BetsTypes = MojitoServices.Bets.types;
const { types: BetslipTypes, utils: BetslipUtils } = MojitoServices.Betslip;
const { ODDS_BOOST_BANNER_STATE } = BetslipTypes;
const {
    DateTimeFormatter,
    DateTimeTypes: { DATE_TIME_FORMAT },
} = MojitoPresentation.Utils.DateTime;
const { FlexPane, Image, Text, ImageButton, Button, Popover } = MojitoPresentation.Components;

const OddsBoostBanner = props => {
    const {
        mojitoTools: { config, style, stringResolver, appContext },
        oddsBoostToken: { boosts, state },
        legSort,
        betId,
        betWay,
        currencyCode,
    } = props;

    const { APPLIED, AVAILABLE, IN_USE } = ODDS_BOOST_BANNER_STATE;
    const isAppliedState = state === APPLIED;
    const isInUseState = state === IN_USE;
    const isAvailableState = state === AVAILABLE;
    const isMultiBoosts = boosts.length > 1;
    const isBetBuilder = legSort === BetsTypes.LEG_SORT.BET_BUILDER;
    const shouldShowInfoTooltip = isAppliedState || (isAvailableState && !isMultiBoosts);
    const labelMap = {
        TITLE: {
            PROFIT: `$ODDS_BOOST_BANNER.${state}.BOOST_PROFIT_TITLE`,
            RETURN: `$ODDS_BOOST_BANNER.${state}.BOOST_RETURNS_TITLE`,
            FIXED: `$ODDS_BOOST_BANNER.${state}.BOOST_FIXED_TITLE`,
        },
        TITLE_WITH_VALUE: {
            PROFIT: `$ODDS_BOOST_BANNER.${state}.BOOST_PROFIT_TITLE_PERCENT`,
            RETURN: `$ODDS_BOOST_BANNER.${state}.BOOST_RETURNS_TITLE_PERCENT`,
            FIXED: `$ODDS_BOOST_BANNER.${state}.BOOST_FIXED_TITLE_PRICE`,
        },
        RETURN_TEXT: {
            PROFIT: '$ODDS_BOOST_BANNER.APPLIED.BOOST_PROFIT',
            RETURN: '$ODDS_BOOST_BANNER.APPLIED.BOOST_RETURNS',
            FIXED: '',
        },
    };

    // when state is APPLIED we will always receive array of a 1 boost -> we can pick first item from array
    // when state is AVAILABLE and there are only 1 boost -> we can pick first item from array
    const selectedBoost = boosts[0];
    const [showDialog, setShowDialog] = useState(false);
    const dispatch = useDispatch();

    const onActionButtonClick = () => {
        switch (state) {
            case AVAILABLE:
                if (isMultiBoosts) {
                    setShowDialog(true);
                } else {
                    applyBoost(selectedBoost.id);
                }
                break;
            case APPLIED:
                removeBoost();
                break;
        }
    };

    const applyBoost = selectedBoostId => {
        dispatch(
            betslipActions.addOddsBoostToken({
                token: selectedBoostId,
                betId,
                betWay,
            })
        );
    };

    const removeBoost = () => {
        dispatch(
            betslipActions.removeOddsBoostToken({
                token: selectedBoost.id,
                betId,
                betWay,
            })
        );
    };

    const getBoostTitle = () => {
        if (isInUseState) {
            return stringResolver.resolveAndFormatString(`$ODDS_BOOST_BANNER.IN_USE.TITLE`);
        }

        if (isAvailableState && isMultiBoosts) {
            return stringResolver.resolveAndFormatString(
                '$ODDS_BOOST_BANNER.AVAILABLE.MULTI_TITLE'
            );
        }

        const contributionSource = selectedBoost?.contributionSource;
        const oddsBoostSubtype = selectedBoost?.oddsBoostSubtype;
        const value = selectedBoost?.value;

        if (!value && !isMultiBoosts) {
            const strKey = BetslipUtils.resolveBoostLabel(contributionSource, oddsBoostSubtype, {
                PROFIT: labelMap.TITLE.PROFIT,
                RETURN: labelMap.TITLE.RETURN,
                FIXED: labelMap.TITLE.FIXED,
            });

            return stringResolver.resolveAndFormatString(strKey);
        }

        const strKey = BetslipUtils.resolveBoostLabel(contributionSource, oddsBoostSubtype, {
            PROFIT: labelMap.TITLE_WITH_VALUE.PROFIT,
            RETURN: labelMap.TITLE_WITH_VALUE.RETURN,
            FIXED: labelMap.TITLE_WITH_VALUE.FIXED,
        });

        return stringResolver.resolveAndFormatString(strKey, value);
    };

    const getReturns = () => {
        const contributionSource = selectedBoost?.contributionSource;
        const oddsBoostSubtype = selectedBoost?.oddsBoostSubtype;

        const strKey = BetslipUtils.resolveBoostLabel(contributionSource, oddsBoostSubtype, {
            PROFIT: labelMap.RETURN_TEXT.PROFIT,
            RETURN: labelMap.RETURN_TEXT.RETURN,
            FIXED: labelMap.RETURN_TEXT.FIXED,
        });

        return stringResolver.resolveAndFormatString(
            strKey,
            CurrencyHelper.formatCurrency(selectedBoost.returns, currencyCode)
        );
    };

    const containerConfig = useMemo(() => {
        if (isBetBuilder) {
            return isAppliedState
                ? style.appliedBetBuilderContainer
                : config.betBuilderContainer.base;
        }
        return isAppliedState ? style.appliedContainer : config.container.base;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBetBuilder, isAppliedState]);

    return (
        <FlexPane
            config={isBetBuilder ? config.betBuilderContainerWrapper : config.containerWrapper}
            class="ta-oddsBoostBanner"
        >
            <FlexPane config={containerConfig}>
                <Image
                    config={isAppliedState ? config.boostIcon.base : style.boostIconDisabled}
                    class="ta-OddsBoostIcon"
                />
                <FlexPane config={config.textContainer}>
                    <Text
                        config={isInUseState ? style.inUseTitle : config.title.base}
                        class="ta-OddsBoostTitle"
                    >
                        {getBoostTitle()}
                    </Text>

                    {isAppliedState && (
                        <Text config={config.returnsText} class="ta-OddsBoostReturns">
                            {getReturns()}
                        </Text>
                    )}
                </FlexPane>
                {shouldShowInfoTooltip && (
                    <InfoTooltip
                        config={config}
                        boost={selectedBoost}
                        appContext={appContext}
                        stringResolver={stringResolver}
                    />
                )}
                <Button
                    config={
                        isAvailableState ? style.availableActionButton : config.actionButton.base
                    }
                    onClick={onActionButtonClick}
                    disabled={isInUseState}
                    class="ta-OddsBoostButton"
                >
                    {stringResolver.resolveString(`$ODDS_BOOST_BANNER.${state}.BUTTON`)}
                </Button>
                {showDialog && (
                    <MultiOddsBoostsDialog
                        boosts={boosts}
                        isOpen={showDialog}
                        onClose={() => setShowDialog(false)}
                        onApply={applyBoost}
                    />
                )}
            </FlexPane>
        </FlexPane>
    );
};

const InfoTooltip = props => {
    const { config, boost, appContext, stringResolver } = props;
    const { value, title, text, expireDate, contributionSource, oddsBoostSubtype } = boost;
    const formattedExpireDate = DateTimeFormatter.formatDateTime(
        expireDate,
        DATE_TIME_FORMAT.NUMERIC_DATE_TIME,
        appContext.userSettings(),
        appContext.systemSettings().language
    );
    // Only FIXED boosts show a plain price/odds value; RETURNS/PROFIT boosts are percentage based and must keep the % sign.
    const valueLabelKey = BetslipUtils.resolveBoostLabel(contributionSource, oddsBoostSubtype, {
        PROFIT: '$ODDS_BOOST_BANNER.PERCENT',
        RETURN: '$ODDS_BOOST_BANNER.PERCENT',
        FIXED: '$ODDS_BOOST_BANNER.VALUE',
    });
    const boostValueText = value ? stringResolver.resolveAndFormatString(valueLabelKey, value) : '';
    const titleLabel = [boostValueText, title].filter(Boolean).join(' ');

    return (
        <Popover
            class="ta-oddsBoostBannerInfoTooltip"
            config={config.popover}
            signpostContent={<ImageButton config={config.infoTooltip.infoButton} />}
        >
            <Text config={config.infoTooltip.title}>{titleLabel}</Text>
            <Text config={config.infoTooltip.text}>{text}</Text>
            {formattedExpireDate && (
                <Text config={config.infoTooltip.expiry}>
                    {`${stringResolver.resolveString('$ODDS_BOOST_BANNER.EXPIRY')} ${formattedExpireDate}`}
                </Text>
            )}
        </Popover>
    );
};

OddsBoostBanner.getStyle = (config, applicationMode, merge) => {
    const { container, betBuilderContainer, title, boostIcon, actionButton } = config;

    return {
        appliedBetBuilderContainer: merge(betBuilderContainer.base, betBuilderContainer.applied),
        appliedContainer: merge(container.base, container.applied),
        inUseTitle: merge(title.base, title.inUse),
        boostIconDisabled: merge(boostIcon.base, boostIcon.disabled),
        availableActionButton: merge(actionButton.base, actionButton.available),
    };
};

export default OddsBoostBanner;
